import React, { useState, useEffect, useMemo } from 'react'
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon,
  PlayIcon,
  MapPinIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { SkeletonCard, SkeletonText, DataLoading } from '../../components/ui/Loading'
import { EmptyState, NetworkError } from '../../components/ui/ErrorBoundary'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'

interface StudentClass {
  id: string
  course: {
    id: string
    title: string
    teacher: {
      user: {
        firstName: string
        lastName: string
      }
    }
  }
  scheduledDate: string
  scheduledTime: string
  duration: number // 分钟
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed'
  type: 'online' | 'offline'
  location?: string
  meetingUrl?: string
  notes?: string
  createdAt: string
}

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  classes: StudentClass[]
}

const StudentSchedule = () => {
  const [classes, setClasses] = useState<StudentClass[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [currentDate, setCurrentDate] = useState<Date>(new Date())
  const [filterStatus, setFilterStatus] = useState<'all' | 'confirmed' | 'pending' | 'cancelled' | 'completed'>('all')
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')

  useEffect(() => {
    fetchClasses()
  }, [])

  const fetchClasses = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('token')
      if (!token) {
        setError('请先登录')
        return
      }
      
      // 调用真实的API
      const response = await fetch('/api/students/schedule', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('获取课表失败')
      }
      
      const data = await response.json()
      setClasses(data)
    } catch (err) {
      console.error('获取课程表失败:', err)
      setError('获取课程表数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 获取当月日历数据
  const getCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    // 获取本月第一天和最后一天
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    
    // 获取本月第一天是星期几（0=周日，1=周一...）
    const firstDayWeek = firstDay.getDay()
    
    const days: CalendarDay[] = []
    
    // 添加上个月的尾部日期
    for (let i = firstDayWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i)
      days.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, new Date()),
        classes: getClassesForDate(date)
      })
    }
    
    // 添加本月的所有日期
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      days.push({
        date,
        isCurrentMonth: true,
        isToday: isSameDay(date, new Date()),
        classes: getClassesForDate(date)
      })
    }
    
    // 添加下个月的开头日期，确保总共42天（6行×7天）
    const remainingDays = 42 - days.length
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day)
      days.push({
        date,
        isCurrentMonth: false,
        isToday: isSameDay(date, new Date()),
        classes: getClassesForDate(date)
      })
    }
    
    return days
  }

  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate()
  }

  const getClassesForDate = (date: Date): StudentClass[] => {
    const dateStr = date.toISOString().split('T')[0]
    return classes.filter(class_ => class_.scheduledDate === dateStr)
  }

  const getFilteredClasses = (): StudentClass[] => {
    if (filterStatus === 'all') {
      return classes
    }
    return classes.filter(class_ => class_.status === filterStatus)
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'confirmed':
        return '已确认'
      case 'pending':
        return '待确认'
      case 'cancelled':
        return '已取消'
      case 'completed':
        return '已完成'
      default:
        return status
    }
  }

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long'
    })
  }

  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5) // HH:MM
  }

  const formatDateTime = (date: string, time: string): string => {
    const dateObj = new Date(date)
    return `${dateObj.getMonth() + 1}月${dateObj.getDate()}日 ${formatTime(time)}`
  }

  const getDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}小时${mins > 0 ? mins + '分钟' : ''}`
    }
    return `${mins}分钟`
  }

  const metrics = useMemo(() => {
    const confirmed = classes.filter((item) => item.status === 'confirmed').length
    const pending = classes.filter((item) => item.status === 'pending').length
    const completed = classes.filter((item) => item.status === 'completed').length

    return [
      {
        label: '当月课程',
        value: classes.length || '—',
        helper: '全部计划'
      },
      {
        label: '已确认',
        value: confirmed || '0',
        helper: '待上课'
      },
      {
        label: '待确认',
        value: pending || '0',
        trend: pending ? `${pending} 门课程需处理` : '全部同步',
        trendColor: pending ? 'negative' : 'positive'
      },
      {
        label: '已完成',
        value: completed || '0',
        helper: '历史学习'
      }
    ]
  }, [classes])

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1)
      } else {
        newDate.setMonth(newDate.getMonth() + 1)
      }
      return newDate
    })
  }

  const goToToday = () => {
    const today = new Date()
    setCurrentDate(today)
    setSelectedDate(today)
  }

  if (error) {
    return (
      <StudentPageLayout
        title="智能课表"
        description="日历与列表视图随时切换，系统会自动同步预约课程与提醒。"
        metrics={metrics}
      >
        <NetworkError onRetry={fetchClasses} />
      </StudentPageLayout>
    )
  }

  return (
    <StudentPageLayout
      title="智能课表"
      description="在一个界面管理所有课程，AI 会根据你的习惯调整提醒时间。"
      metrics={metrics}
      actions={
        <div className="flex gap-3 flex-wrap">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 rounded-full text-sm text-slate-800 bg-white/90 border border-white/50 shadow-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">全部状态</option>
            <option value="confirmed">已确认</option>
            <option value="pending">待确认</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
          <div className="flex border border-white/50 rounded-full overflow-hidden bg-white/20 text-white text-sm">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 transition ${
                viewMode === 'calendar' ? 'bg-white/90 text-slate-900' : 'hover:bg-white/10'
              }`}
            >
              日历视图
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 transition ${
                viewMode === 'list' ? 'bg-white/90 text-slate-900' : 'hover:bg-white/10'
              }`}
            >
              列表视图
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
          <div className="space-y-6">
            <SkeletonCard className="h-64" />
            <SkeletonCard className="h-32" />
          </div>
        ) : viewMode === 'calendar' ? (
          /* 日历视图 */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 日历 */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {/* 日历头部 */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {formatDate(currentDate)}
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={goToToday}
                        className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        今天
                      </button>
                      <button
                        onClick={() => navigateMonth('prev')}
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                      </button>
                      <button
                        onClick={() => navigateMonth('next')}
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* 日历网格 */}
                <div className="p-6">
                  {/* 星期标题 */}
                  <div className="grid grid-cols-7 gap-1 mb-4">
                    {['日', '一', '二', '三', '四', '五', '六'].map(day => (
                      <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* 日期网格 */}
                  <div className="grid grid-cols-7 gap-1">
                    {getCalendarDays().map((day, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedDate(day.date)}
                        className={`
                          relative p-2 h-24 border rounded-lg text-left transition-colors
                          ${day.isCurrentMonth ? 'border-gray-200' : 'border-gray-100'}
                          ${day.isToday ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}
                          ${isSameDay(day.date, selectedDate) ? 'ring-2 ring-blue-500' : ''}
                        `}
                      >
                        <span className={`
                          text-sm font-medium
                          ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                          ${day.isToday ? 'text-blue-600' : ''}
                        `}>
                          {day.date.getDate()}
                        </span>
                        
                        {/* 课程指示器 */}
                        {day.classes.length > 0 && (
                          <div className="absolute bottom-1 left-1 right-1">
                            {day.classes.slice(0, 3).map((class_, classIndex) => (
                              <div
                                key={classIndex}
                                className={`text-xs px-1 rounded mb-0.5 truncate ${getStatusColor(class_.status)}`}
                              >
                                {formatTime(class_.scheduledTime)}
                              </div>
                            ))}
                            {day.classes.length > 3 && (
                              <div className="text-xs text-gray-500">
                                +{day.classes.length - 3}更多
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 选中日期的课程详情 */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDate.toLocaleDateString('zh-CN', {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                  </h3>
                </div>
                
                <div className="p-6">
                  {getClassesForDate(selectedDate).length > 0 ? (
                    <div className="space-y-4">
                      {getClassesForDate(selectedDate).map((class_) => (
                        <div key={class_.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-medium text-gray-900">{class_.course.title}</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(class_.status)}`}>
                              {getStatusText(class_.status)}
                            </span>
                          </div>
                          
                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 mr-2" />
                              {class_.course.teacher.user.firstName}{class_.course.teacher.user.lastName}
                            </div>
                            
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-2" />
                              {formatTime(class_.scheduledTime)} ({getDuration(class_.duration)})
                            </div>
                            
                            <div className="flex items-center">
                              {class_.type === 'online' ? (
                                <PlayIcon className="h-4 w-4 mr-2" />
                              ) : (
                                <MapPinIcon className="h-4 w-4 mr-2" />
                              )}
                              {class_.type === 'online' ? '在线课程' : class_.location}
                            </div>
                          </div>
                          
                          {class_.notes && (
                            <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                              {class_.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState 
                      title="当天没有课程"
                      description="选择其他日期查看课程安排"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* 列表视图 */
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">课程列表</h2>
            </div>
            
            <div className="p-6">
              {getFilteredClasses().length > 0 ? (
                <div className="space-y-4">
                  {getFilteredClasses().map((class_) => (
                    <div key={class_.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">{class_.course.title}</h3>
                          <div className="flex items-center text-sm text-gray-600 mb-2">
                            <UserIcon className="h-4 w-4 mr-1" />
                            {class_.course.teacher.user.firstName}{class_.course.teacher.user.lastName}
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(class_.status)}`}>
                          {getStatusText(class_.status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          {formatDateTime(class_.scheduledDate, class_.scheduledTime)}
                        </div>
                        
                        <div className="flex items-center text-gray-600">
                          <ClockIcon className="h-4 w-4 mr-2" />
                          {getDuration(class_.duration)}
                        </div>
                        
                        <div className="flex items-center text-gray-600">
                          {class_.type === 'online' ? (
                            <>
                              <PlayIcon className="h-4 w-4 mr-2" />
                              在线课程
                            </>
                          ) : (
                            <>
                              <MapPinIcon className="h-4 w-4 mr-2" />
                              {class_.location}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {class_.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                          <strong>备注：</strong> {class_.notes}
                        </div>
                      )}
                      
                      {class_.status === 'confirmed' && class_.type === 'online' && class_.meetingUrl && (
                        <div className="mt-4">
                          <a
                            href={class_.meetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <PlayIcon className="h-4 w-4 mr-2" />
                            进入课堂
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState 
                  title="暂无课程"
                  description={filterStatus === 'all' ? '您还没有预约任何课程' : `没有找到${getStatusText(filterStatus)}的课程`}
                  actionText="浏览课程"
                  onAction={() => window.location.href = '/courses'}
                />
              )}
            </div>
          </div>
        )}
    </StudentPageLayout>
  )
}

export default StudentSchedule 